import { router } from '@inertiajs/vue3'

import { getConnectionId } from './Composables/useRealtimeToken'

const HEADER = 'X-Realtime-Connection'

export interface HeaderCarrier {
  setHeader(name: string, value: string): void
}

export interface RequestInterceptorHost {
  use(handler: (config: Record<string, unknown>) => Record<string, unknown>): number
  eject(id: number): void
}

/**
 * Install the X-Realtime-Connection header on every outgoing Inertia visit.
 * Inertia exposes router.on('before') for this purpose in v3.
 */
export function installOnInertia(): () => void {
  const remove = router.on('before', (event) => {
    event.detail.visit.headers[HEADER] = getConnectionId()
  })
  return remove
}

/**
 * Generic request-transformer installer.
 *
 * Accepts any client exposing a request interceptor API of the shape
 * `use(fn) → id` / `eject(id)`. Matches axios-style transformers and the
 * @ja/portal-query client transformer pipeline.
 */
export function installOnRequestInterceptor(host: RequestInterceptorHost): () => void {
  const id = host.use((config) => {
    const headers = ((config as { headers?: Record<string, string> }).headers ?? {}) as Record<
      string,
      string
    >
    headers[HEADER] = getConnectionId()
    ;(config as { headers: Record<string, string> }).headers = headers
    return config
  })
  return () => host.eject(id)
}

/**
 * Install a global fetch wrapper for the non-Inertia useHttp client.
 * Returns an uninstall function; test harnesses only.
 */
export function installOnFetch(target: { fetch: typeof fetch } = globalThis as unknown as { fetch: typeof fetch }): () => void {
  const original = target.fetch.bind(target)

  const wrapped: typeof fetch = (input, init = {}) => {
    const headers = new Headers(init.headers ?? undefined)
    if (!headers.has(HEADER)) {
      headers.set(HEADER, getConnectionId())
    }
    return original(input, { ...init, headers })
  }

  target.fetch = wrapped
  return () => {
    target.fetch = original
  }
}

/**
 * Install the header across every supported client surface. Call once at app
 * bootstrap; returns a combined uninstaller for test tear-down.
 */
export function installRealtimeRequestHeader(): () => void {
  const uninstallers: Array<() => void> = [installOnInertia(), installOnFetch()]

  return () => {
    uninstallers.forEach((fn) => fn())
  }
}
